import { useState, useCallback, useRef } from 'react'
import { parseStreamEvent } from './sseEvents'
import { RETAILER_AGENT_BASE_URL, getSessionToken } from './config'

const STORED_SESSION_KEY_PREFIX = 'retailer_agent_session_id'

function writeStoredSessionId(partnerId: string, sessionId: string): void {
  try {
    localStorage.setItem(`${STORED_SESSION_KEY_PREFIX}_${partnerId}`, sessionId)
  } catch {
    // ignore storage errors
  }
}

function clearStoredSessionId(partnerId: string): void {
  try {
    localStorage.removeItem(`${STORED_SESSION_KEY_PREFIX}_${partnerId}`)
  } catch {
    // ignore storage errors
  }
}

function readStoredSessionId(partnerId: string): string | null {
  try {
    return localStorage.getItem(`${STORED_SESSION_KEY_PREFIX}_${partnerId}`)
  } catch {
    return null
  }
}

export type MessageRole = 'user' | 'assistant'

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  toolName?: string
}

export interface ApprovalState {
  suspensionToken: string
  summary: string
  toolName: string
}

export interface RetailerAgentChatState {
  messages: ChatMessage[]
  isStreaming: boolean
  isToolRunning: boolean
  isRestoringHistory: boolean
  toolName: string | null
  error: string | null
  approval: ApprovalState | null
  sendMessage: (message: string, partnerId: string, allowedTools?: string[]) => Promise<void>
  confirmApproval: (partnerId: string) => Promise<void>
  cancelApproval: () => void
  stopStreaming: () => void
  startNewChat: (partnerId: string) => void
  switchToSession: (sessionId: string, partnerId: string) => Promise<void>
  readStoredSessionId: (partnerId: string) => string | null
}

export function useRetailerAgentChat(): RetailerAgentChatState {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [isToolRunning, setIsToolRunning] = useState(false)
  const [isRestoringHistory, setIsRestoringHistory] = useState(false)
  const [toolName, setToolName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [approval, setApproval] = useState<ApprovalState | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const appendAssistantChunk = useCallback((chunk: string) => {
    setMessages(prev => {
      const last = prev[prev.length - 1]
      if (last?.role === 'assistant') {
        return [...prev.slice(0, -1), { ...last, content: last.content + chunk }]
      }
      return [...prev, { id: crypto.randomUUID(), role: 'assistant', content: chunk }]
    })
  }, [])

  const runStream = useCallback(
    async (url: string, body: Record<string, unknown>, partnerId: string) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setIsStreaming(true)
      setError(null)

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getSessionToken() ?? ''}`,
            'X-IPP-Partner-Ids': partnerId,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        })

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          setError(`Request failed (${response.status}): ${text}`)
          return
        }

        const reader = response.body?.getReader()
        if (!reader) {
          setError('No response body')
          return
        }

        const decoder = new TextDecoder()
        let buffer = ''

        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''

            for (const line of lines) {
              const trimmed = line.trim()
              if (!trimmed.startsWith('data: ')) continue
              const data = trimmed.slice(6)
              if (data === '[DONE]') break

              let json: unknown
              try {
                json = JSON.parse(data)
              } catch {
                continue
              }

              const event = parseStreamEvent(json)
              if (!event) continue

              if ('type' in event) {
                switch (event.type) {
                  case 'session_created':
                    sessionIdRef.current = event.session_id
                    writeStoredSessionId(partnerId, event.session_id)
                    break
                  case 'text_delta':
                    appendAssistantChunk(event.text)
                    break
                  case 'tool_completed':
                    setIsToolRunning(false)
                    setToolName(null)
                    break
                  case 'approval_required':
                    setApproval({
                      suspensionToken: event.suspension_token,
                      summary: event.preview?.summary ?? 'Confirm action',
                      toolName: event.tool_name ?? '',
                    })
                    break
                  case 'provider_error':
                    setError(event.message ?? 'An error occurred')
                    break
                }
              } else if (event.partial === true) {
                // ADK native streaming event
                for (const part of event.content?.parts ?? []) {
                  if (part.text) {
                    appendAssistantChunk(part.text)
                  } else if (part.function_call) {
                    setIsToolRunning(true)
                    setToolName(part.function_call.name)
                  } else if (part.function_response) {
                    setIsToolRunning(false)
                    setToolName(null)
                  }
                }
              }
            }
          }
        } finally {
          reader.releaseLock()
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError((err as Error).message)
        }
      } finally {
        setIsStreaming(false)
        setIsToolRunning(false)
        setToolName(null)
      }
    },
    [appendAssistantChunk]
  )

  const sendMessage = useCallback(
    async (message: string, partnerId: string, allowedTools: string[] = []) => {
      setApproval(null)
      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user', content: message }])

      await runStream(
        `${RETAILER_AGENT_BASE_URL}/stream`,
        {
          message,
          allowed_tools: allowedTools,
          session_id: sessionIdRef.current,
          partner_id: partnerId,
        },
        partnerId
      )
    },
    [runStream]
  )

  const confirmApproval = useCallback(
    async (partnerId: string) => {
      if (!approval || !sessionIdRef.current) return
      const token = approval.suspensionToken
      const sid = sessionIdRef.current
      setApproval(null)

      await runStream(
        `${RETAILER_AGENT_BASE_URL}/resume`,
        { session_id: sid, suspension_token: token, approved: true, tool_name: approval.toolName },
        partnerId
      )
    },
    [approval, runStream]
  )

  const cancelApproval = useCallback(() => {
    setApproval(null)
    // Session remains suspended in DB until TTL expires (15 min) — acceptable for MVP.
  }, [])

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const startNewChat = useCallback((partnerId: string) => {
    sessionIdRef.current = null
    clearStoredSessionId(partnerId)
    setMessages([])
    setApproval(null)
    setError(null)
  }, [])

  const switchToSession = useCallback(async (sessionId: string, partnerId: string) => {
    setIsRestoringHistory(true)
    try {
      const response = await fetch(`${RETAILER_AGENT_BASE_URL}/sessions/${sessionId}/messages`, {
        headers: {
          Authorization: `Bearer ${getSessionToken() ?? ''}`,
          'X-IPP-Partner-Ids': partnerId,
        },
      })

      if (!response.ok) return

      const data = (await response.json()) as {
        messages: Array<{ role: string; content: string }>
      }

      const restoredMessages: ChatMessage[] = (data.messages ?? []).map(m => ({
        id: crypto.randomUUID(),
        role: m.role === 'user' ? ('user' as const) : ('assistant' as const),
        content: m.content,
      }))

      sessionIdRef.current = sessionId
      writeStoredSessionId(partnerId, sessionId)
      setMessages(restoredMessages)
      setApproval(null)
      setError(null)
    } catch {
      // silently fail — user can still chat normally
    } finally {
      setIsRestoringHistory(false)
    }
  }, [])

  return {
    messages,
    isStreaming,
    isToolRunning,
    isRestoringHistory,
    toolName,
    error,
    approval,
    sendMessage,
    confirmApproval,
    cancelApproval,
    stopStreaming,
    startNewChat,
    switchToSession,
    readStoredSessionId,
  }
}
